<template>
  <Admin>
    <v-main>
      <v-container fluid>
        <v-row justify="center">
          <v-col>
            <!-- Same frame as the other document pages (documents.vue ->
                 table.vue): plain <Admin>, one default v-card holding the
                 toolbar in its v-card-title and the table below it. -->
            <v-card class="submitted-docs">
              <v-card-title class="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                <div class="search-box">
                  <v-text-field
                    v-model="keywordInput"
                    :label="searchLabel"
                    :placeholder="searchLabel"
                    append-icon="mdi-magnify"
                    outlined
                    dense
                    hide-details
                    clearable
                    :disabled="!isLaoTaxRis"
                  ></v-text-field>
                </div>
                <v-chip-group
                  v-model="typeFilter"
                  mandatory
                  active-class="submitted-docs__chip--active"
                >
                  <v-chip v-for="t in typeOptions" :key="t.value" :value="t.value" small outlined>
                    {{ t.text }}
                    <span class="tw-ml-1 tw-text-gray-500">{{ t.count }}</span>
                  </v-chip>
                </v-chip-group>
                <v-spacer></v-spacer>
                <div class="month-box">
                  <v-select
                    v-model="monthFilter"
                    :items="monthOptions"
                    label="เดือนเอกสาร"
                    append-icon="mdi-calendar-month-outline"
                    outlined
                    dense
                    hide-details
                    :disabled="!isLaoTaxRis"
                  ></v-select>
                </div>
                <div class="sap-box">
                  <v-select
                    v-model="sapFilter"
                    :items="sapOptions"
                    label="การซิงค์ SAP"
                    append-icon="mdi-database-arrow-right-outline"
                    outlined
                    dense
                    hide-details
                    :disabled="!isLaoTaxRis"
                  ></v-select>
                </div>
                <v-btn
                  v-if="hasFilter"
                  text
                  height="40"
                  class="toolbar-btn"
                  color="grey darken-1"
                  @click="clearFilters"
                >
                  <v-icon left small>mdi-filter-remove-outline</v-icon>
                  ล้างตัวกรอง
                </v-btn>
                <approved-export-dialog
                  :rows="filteredRows"
                  :extra-columns="exportExtraColumns"
                  :filters="exportFilters"
                  :period="monthFilter"
                  :type-label="(code) => typeMeta(code).label"
                  :sap-label="sapLabel"
                  :format-issue-time="formatIssueTime"
                ></approved-export-dialog>
              </v-card-title>

              <!-- Its own v-if, outside the chain below: a capped result is
                   shown WITH the table, not instead of it. -->
              <v-alert v-if="capHit" type="warning" text dense class="tw-mx-4">
                "ทุกเดือน" แสดงได้สูงสุด {{ allMonthsLimit.toLocaleString() }} รายการล่าสุด
                และอาจมีมากกว่านี้ — เลือกเดือนเอกสารเพื่อดูครบทั้งเดือน
                (ไฟล์ Excel จะได้เท่าที่แสดงอยู่เท่านั้น)
              </v-alert>
              <!-- Only the descending probe needs the second index, so this is
                   what one-of-two-indexes looks like. The table still works,
                   hence a warning and not the error banner. -->
              <v-alert v-if="monthRangeFailed" type="warning" text dense class="tw-mx-4">
                หาเดือนล่าสุดที่มีเอกสารไม่ได้ จึงเปิดที่เดือนปัจจุบันไว้ก่อน —
                เลือกเดือนจากรายการได้ตามปกติ ถ้าอยากให้เปิดที่เดือนล่าสุดเอง
                และใช้ "ทุกเดือน" ได้ ต้องสร้าง index ตัวที่สองบน Docs:
                `@LaoEtaxStatus` (Ascending) + `ED.IssueDateTime`
                (<b>Descending</b>)
              </v-alert>
              <v-alert
                v-if="!isProductListLoading && !isLaoTaxRis"
                type="info"
                text
                dense
                class="tw-mx-4"
              >
                หน้านี้ใช้สำหรับบริษัทที่เปิดใช้ Lao TaxRIS เท่านั้น
              </v-alert>
              <v-alert v-else-if="error" type="error" text dense class="tw-mx-4">
                โหลดรายการไม่สำเร็จ — {{ error }}
              </v-alert>

              <!-- items-per-page-options carries no -1 ("ทั้งหมด"):
                   v-data-table has no virtual scroll and every row here mounts
                   a tooltip, an icon and a button, so that option froze the tab
                   on a busy month. -->
              <v-data-table
                v-else
                :headers="headers"
                :items="filteredRows"
                :footer-props="{ 'items-per-page-options': [20, 50, 100, 200], 'items-per-page-text': 'แถวต่อหน้า' }"
                :items-per-page="50"
                sort-by="issueTime"
                sort-desc
              >
                <template v-slot:[`item.id`]="{ item }">
                  <div>{{ item.id }}</div>
                  <div class="tw-text-xs tw-text-gray-500">{{ item.issueDate || "-" }}</div>
                </template>
                <template v-slot:[`item.docType`]="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        v-bind="attrs"
                        x-small
                        label
                        :color="typeMeta(item.docType).color"
                        text-color="white"
                        v-on="on"
                      >{{ item.docType }}</v-chip>
                    </template>
                    <span>{{ typeMeta(item.docType).label }}</span>
                  </v-tooltip>
                </template>
                <template v-slot:[`item.buyerName`]="{ item }">
                  <span class="submitted-docs__clip" :title="item.buyerName">{{ item.buyerName || "-" }}</span>
                </template>
                <template v-slot:[`item.buyerTaxID`]="{ item }">
                  <span class="submitted-docs__num">{{ item.buyerTaxID || "-" }}</span>
                </template>
                <template v-slot:[`item.grandTotal`]="{ item }">
                  <span class="submitted-docs__num">{{ formatAmount(item.grandTotal) }}</span>
                </template>
                <template v-slot:[`item.invoiceNumber`]="{ item }">
                  {{ item.invoiceNumber || "-" }}
                </template>
                <template v-slot:[`item.sapState`]="{ item }">
                  <v-tooltip bottom max-width="360">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-bind="attrs"
                        small
                        :color="sapMeta(item).color"
                        :aria-label="sapAriaLabel(item)"
                        v-on="on"
                      >{{ sapMeta(item).icon }}</v-icon>
                    </template>
                    <span style="white-space: pre-line; overflow-wrap: anywhere">{{
                      sapTooltip(item)
                    }}</span>
                  </v-tooltip>
                </template>
                <template v-slot:[`item.issueTime`]="{ item }">
                  {{ formatIssueTime(item.issueTime) }}
                </template>
                <template v-slot:[`item.url`]="{ item }">
                  <v-btn
                    v-if="item.url"
                    text
                    small
                    color="#6ec39b"
                    :href="item.url"
                    target="_blank"
                    rel="noopener"
                    :aria-label="'เปิดหน้าตรวจสอบ Lao eTax (' + item.id + ')'"
                  >
                    ตรวจสอบ
                    <v-icon right x-small>mdi-open-in-new</v-icon>
                  </v-btn>
                  <span v-else class="tw-text-gray-300">-</span>
                </template>

                <!-- v-data-table only reaches its `no-results` slot when its
                     own `search` prop is set, which this table does not use —
                     so every empty case lands here and has to be told apart
                     by hand. Naming the month matters: the page is bounded to
                     one, and "ยังไม่มีเอกสารที่อนุมัติ" on a month that simply
                     has none reads as a page that failed to load. -->
                <template v-slot:no-data>
                  <div class="tw-py-6">
                    <template v-if="isLoading">กำลังโหลด...</template>
                    <template v-else-if="rows.length">
                      ไม่พบเอกสารตามตัวกรอง
                      <v-btn text small color="#6ec39b" @click="clearFilters">ล้างตัวกรอง</v-btn>
                    </template>
                    <template v-else-if="monthFilter === 'all'">
                      ยังไม่มีเอกสารที่อนุมัติ
                    </template>
                    <template v-else>
                      ไม่มีเอกสารที่อนุมัติใน{{ selectedMonthLabel }}
                      <v-btn
                        v-if="newestMonth && newestMonth !== monthFilter"
                        text
                        small
                        color="#6ec39b"
                        @click="monthFilter = newestMonth"
                      >
                        ไปเดือนล่าสุดที่มีเอกสาร ({{ newestMonthLabel }})
                      </v-btn>
                    </template>
                  </div>
                </template>
              </v-data-table>
              <!-- Loading bar as on the other document pages (table.vue): a thin
                   bar at the foot of the card. Not v-data-table's `loading`
                   prop, which paints a primary-green band under the header. -->
              <v-progress-linear
                indeterminate
                color="green"
                :active="isLoading"
              ></v-progress-linear>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </Admin>
</template>

<script>
/**
 * Documents the Lao MoF eTax gateway has approved (@LaoEtaxStatus ==
 * 'approved', see constants/laoEtaxStatus.js). Everything still in flight,
 * rejected, failed or cancelled stays on the normal document pages only.
 *
 * `cancelled` is deliberately left out. An approved Lao invoice cannot be
 * cancelled — it is corrected with a credit note, which shows here as a CR row.
 * cancelLaoEtaxInvoice only accepts `invalid` documents, and the verification
 * poller only maps `issued` ones, so a cancelled document was never a valid
 * tax invoice and does not belong in this register.
 *
 * Live, so a document appears here the moment verification approves it.
 *
 * ── One month at a time ───────────────────────────────────────────────────
 *
 * The month filter IS the query, and it defaults to this month, so the reads
 * per page open do not grow with the company's history. It used to listen to
 * the whole approved collection and filter in memory — libs/laoApprovedQuery.js
 * has the full account, the two composite indexes this needs, and why ranging
 * on ED.IssueDateTime cannot drop a row.
 *
 * "ทุกเดือน" is still offered and still useful (searching across months), but
 * it is capped and SAYS SO when the cap bites — in the banner below and again
 * in the export dialog, since a silently truncated tax register is worse than
 * a slow one.
 *
 * ── What is still client-side, and why ────────────────────────────────────
 *
 * Type, keyword and SAP state filter in memory, over one month of rows. Type
 * and keyword could be queries but would each need their own index for no
 * gain on a set this size; the SAP state could not be queried at all, since
 * "the connector has not touched this document" is the ABSENCE of
 * @LaoEtaxSapSynced. The keyword is debounced instead — see applyKeyword.
 *
 * Per-company extra columns (Settings/laoEtax.extraColumns, e.g. เลขที่สัญญา)
 * are shown and searched too. Being client-side they need no index of their
 * own — and they are sortable, since nothing is dropped by a Firestore
 * orderBy that never runs on them.
 *
 * The drawer only offers this page to companies with isLaoTaxRis; the check
 * below covers someone arriving by URL.
 */
import { mapState } from "vuex";
import moment from "moment";
import debounce from "lodash/debounce";
import "moment/locale/th";
import Admin from "../../components/admin/admin-main-template";
import fc from "@/firebaseConfig";
import formatTwoDecimal from "@/libs/formatTwoDecimal";
import {
  ALL_MONTHS,
  ALL_MONTHS_LIMIT,
  buildApprovedQuery,
  probeApprovedMonths,
  describeQueryError,
  thisMonth,
} from "@/libs/laoApprovedQuery";
import { resolveLaoExtraColumns } from "@/constants/laoEtaxColumns";
import {
  SAP_SYNC_META,
  SAP_SYNC_FILTER_ORDER,
  sapIsTouched,
  sapSyncError,
  sapSyncState,
  sapSyncLabel,
  sapSyncTooltip,
} from "@/constants/laoSapSync";
import ApprovedExportDialog from "@/components/admin/documents/approved-export-dialog.vue";

const DOC_TYPE_CODE = { 1: "TX", 4: "CR", 6: "RT" };
const DOC_TYPE_META = {
  RT: { label: "ใบเสร็จรับเงิน/ใบกำกับภาษี", color: "#3f9e73" },
  TX: { label: "ใบกำกับภาษี", color: "#1e88e5" },
  CR: { label: "ใบลดหนี้", color: "#fb8c00" },
};
// ALL_MONTHS is the month filter's "every month"; the type and SAP filters
// use the same token for their own "all", which is why it is aliased here.
const ALL = ALL_MONTHS;

// Extra-column keys are Firestore field paths ("@DocData.policyNo"): dots mean
// nesting. Rows get a flat "extra_<id>" property instead, because
// v-data-table would also read a dotted `value` as a nested path.
function readFieldPath(data, path) {
  return path.split(".").reduce((o, k) => (o && typeof o === "object" ? o[k] : undefined), data);
}
const extraProp = (col) => "extra_" + col.id;

export default {
  name: "DocumentsApproved",
  components: { Admin, ApprovedExportDialog },
  data() {
    const current = thisMonth();
    return {
      companyID: this.$route.params.companyId,
      loading: false,
      error: "",
      // Keyed by document ID so a snapshot can patch the rows that changed —
      // see applyChanges. `rows` is the array view of it.
      rowsById: {},
      // What the search box holds vs. what the filter runs on; applyKeyword
      // moves the first to the second on a trailing debounce.
      keywordInput: "",
      keyword: "",
      typeFilter: ALL,
      // The query's month, not a client-side filter. Stored alongside the
      // default so "ล้างตัวกรอง" returns here rather than to ทุกเดือน, which
      // would quietly turn the cheap query back into the expensive one.
      defaultMonth: current,
      monthFilter: current,
      sapFilter: ALL,
      // Months offered in the dropdown, newest first. Generated from the
      // oldest and newest approved documents (loadMonthRange), because a
      // single-month query can no longer tell us which months exist.
      monthRange: [current],
      // True while "ทุกเดือน" is showing ALL_MONTHS_LIMIT rows and there may
      // be more. Surfaced in the template — never a silent truncation.
      capHit: false,
      // The newest month that has documents, and the month the page opens on.
      newestMonth: "",
      // The month probe threw — almost always the second index missing. Shown
      // as a warning, because the table itself still works.
      monthRangeFailed: false,
      unsubscribe: null,
      // Bumped per subscription. unsubscribe() cannot recall a snapshot
      // already in flight, so a stale one is dropped by comparing this.
      queryGeneration: 0,
      // Whether the current subscription has delivered its first snapshot.
      // That one is applied in bulk; later ones are patched.
      primed: false,
      extraColumns: [],
      // init() runs once. Two flags, not one, because it awaits: initStarted
      // closes the door synchronously against the isLaoTaxRis watcher firing
      // again while the settings read is in flight, and extraColumnsReady —
      // set only once the columns are actually in hand — is what lets the
      // monthFilter watcher open a listener of its own.
      initStarted: false,
      extraColumnsReady: false,
      baseHeaders: [
        { text: "เลขที่เอกสาร", value: "id" },
        { text: "ประเภท", value: "docType", width: 90 },
        { text: "ชื่อผู้ซื้อ", value: "buyerName" },
        // SCTT.AHTA.BTP.STR.ID — the BUYER's TIN. The seller's is the company
        // itself and would be the same on every row. "เลขผู้เสียภาษี" is what
        // documents/table.vue calls this same field.
        { text: "เลขผู้เสียภาษี", value: "buyerTaxID", width: 140 },
        { text: "ยอดรวม", value: "grandTotal", align: "end" },
        { text: "เลขที่ Lao eTax", value: "invoiceNumber" },
        // Sorted by the state key, which orders failed -> pending -> synced.
        // "การซิงค์ SAP", the same name documents/table.vue gives this column
        // (its <th> at the SAP icon), so the two pages do not have two names
        // for one thing. The width holds that label AND the sort arrow on one
        // line — the cell below it is a single icon, but the header is what
        // sets the column's width. table.vue's 90px is for a header with no
        // arrow to make room for.
        { text: "การซิงค์ SAP", value: "sapState", align: "center", width: 130 },
        { text: "เวลานำส่ง", value: "issueTime", width: 170 },
        { text: "", value: "url", align: "end", sortable: false, width: 120 },
      ],
    };
  },
  computed: {
    ...mapState({
      productList: (state) => state.companies.selectedCompanyProductList,
      isProductListLoading: (state) => state.companies.isProductListLoading,
    }),
    isLaoTaxRis() {
      return !!(this.productList && this.productList.isLaoTaxRis);
    },
    // The array view of rowsById. Rebuilding this on a change is a key walk,
    // not a re-map of document data, which is what toRow() per row cost.
    rows() {
      return Object.values(this.rowsById);
    },
    // For the cap notice's copy.
    allMonthsLimit() {
      return ALL_MONTHS_LIMIT;
    },
    selectedMonthLabel() {
      return this.monthLabel(this.monthFilter);
    },
    newestMonthLabel() {
      return this.monthLabel(this.newestMonth);
    },
    isLoading() {
      return this.loading || this.isProductListLoading;
    },
    // Extra columns go right after the buyer, where the documents table puts them.
    headers() {
      const extra = this.extraColumns.map((c) => ({ text: c.label, value: extraProp(c) }));
      // After the whole buyer block (name + TIN), which is where the documents
      // table puts them too — a per-company identifier like เลขที่สัญญา reads
      // as extra detail about a buyer already identified, not as part of
      // identifying them.
      const at = this.baseHeaders.findIndex((h) => h.value === "buyerTaxID") + 1;
      return this.baseHeaders.slice(0, at).concat(extra, this.baseHeaders.slice(at));
    },
    searchLabel() {
      const parts = ["เลขที่เอกสาร", "ผู้ซื้อ", "เลขผู้เสียภาษี", "เลขที่ Lao eTax"].concat(
        this.extraColumns.map((c) => c.label)
      );
      return "ค้นหา " + parts.join(" / ");
    },
    // Only the types this company actually has, so no chip ever filters to nothing.
    typeOptions() {
      const counts = {};
      this.rows.forEach((r) => { counts[r.docType] = (counts[r.docType] || 0) + 1; });
      const types = Object.keys(counts).map((code) => ({
        text: (DOC_TYPE_META[code] && DOC_TYPE_META[code].label) || code,
        value: code,
        count: counts[code],
      }));
      return [{ text: "ทั้งหมด", value: ALL, count: this.rows.length }].concat(types);
    },
    // Built from monthRange, not from the rows on screen: the query loads one
    // month now, so the rows only ever know about themselves. The selected
    // month and this month are forced in, so the dropdown can never fail to
    // show what it is currently set to — including before the probe returns.
    monthOptions() {
      const months = this.monthRange.slice();
      [this.monthFilter, this.defaultMonth].forEach((m) => {
        if (m && m !== ALL && months.indexOf(m) === -1) months.push(m);
      });
      months.sort().reverse();
      return [{ text: "ทุกเดือน", value: ALL }].concat(
        months.map((m) => ({ text: this.monthLabel(m), value: m }))
      );
    },
    // Every state is always offered, counts and all: unlike the type chips, a
    // bucket that is empty is the answer someone is looking for — "nothing
    // failed" has to be visible without having to select it.
    sapOptions() {
      const counts = {};
      this.rows.forEach((r) => {
        counts[r.sapState] = (counts[r.sapState] || 0) + 1;
      });
      return [{ text: "ทั้งหมด (" + this.rows.length + ")", value: ALL }].concat(
        SAP_SYNC_FILTER_ORDER.map((s) => ({
          text: SAP_SYNC_META[s].label + " (" + (counts[s] || 0) + ")",
          value: s,
        }))
      );
    },
    // The company's optional columns as the export dialog wants them.
    exportExtraColumns() {
      return this.extraColumns.map((c) => ({ label: c.label, prop: extraProp(c) }));
    },
    // Filters in effect, restated in the export confirm dialog.
    exportFilters() {
      const month = this.monthOptions.find((m) => m.value === this.monthFilter);
      const type = this.typeOptions.find((t) => t.value === this.typeFilter);
      const out = [
        { label: "เดือนเอกสาร", value: month ? month.text : "ทุกเดือน" },
        { label: "ประเภท", value: type ? type.text : "ทั้งหมด" },
        // The bare label, not sapOptions' text: the count in there is of all
        // rows in that state, which is not what the file will contain once the
        // month and type filters have had their say.
        {
          label: "การซิงค์ SAP",
          value: this.sapFilter === ALL ? "ทั้งหมด" : sapSyncLabel(this.sapFilter),
        },
      ];
      if (this.keyword) out.push({ label: "คำค้นหา", value: this.keyword });
      // The cap belongs here too, not only in the page's banner: the file is
      // what someone files or reconciles, and it is the one place a missing
      // document cannot be noticed later.
      if (this.capHit) {
        out.push({
          label: "ข้อจำกัด",
          value: "เฉพาะ " + ALL_MONTHS_LIMIT.toLocaleString() + " รายการล่าสุด",
        });
      }
      return out;
    },
    // Against the DEFAULTS, not against "no filter at all": this month is the
    // resting state of the page, so showing "ล้างตัวกรอง" for it would mark
    // the page permanently filtered and offer to clear nothing.
    hasFilter() {
      return !!(
        this.keywordInput ||
        this.typeFilter !== ALL ||
        this.monthFilter !== this.defaultMonth ||
        this.sapFilter !== ALL
      );
    },
    // The month is gone from here — it is the query. What is left runs over
    // one month of rows.
    filteredRows() {
      const kw = (this.keyword || "").trim().toLowerCase();
      const extraProps = this.extraColumns.map(extraProp);
      return this.rows.filter((r) => {
        if (this.typeFilter !== ALL && r.docType !== this.typeFilter) return false;
        if (this.sapFilter !== ALL && r.sapState !== this.sapFilter) return false;
        if (!kw) return true;
        return [r.id, r.buyerName, r.buyerTaxID, r.invoiceNumber]
          .concat(extraProps.map((p) => r[p]))
          .some(
            (v) => String(v || "").toLowerCase().indexOf(kw) !== -1
          );
      });
    },
  },
  watch: {
    isLaoTaxRis: {
      immediate: true,
      handler(active) {
        if (active) this.init();
      },
    },
    // The month IS the query, so changing it replaces the listener.
    monthFilter() {
      if (this.isLaoTaxRis && this.extraColumnsReady) this.subscribe();
    },
    keywordInput() {
      this.applyKeyword();
    },
  },
  created() {
    // Per instance, not on the options object: a debounced function declared
    // in `methods` would be shared by every mount of this component.
    //
    // 300ms. filteredRows walks every loaded row across every searchable
    // column and v-data-table re-renders the page on each change, so typing
    // "MSIG" used to do all of that four times, once per keystroke.
    this.applyKeyword = debounce(function () {
      this.keyword = (this.keywordInput || "").trim();
    }, 300);
  },
  beforeDestroy() {
    this.teardown();
    this.applyKeyword.cancel();
  },
  methods: {
    // Resolve what the rows need, once per company, then open the listener.
    // Kept apart from subscribe() so changing the month does not re-read the
    // settings document.
    async init() {
      if (this.initStarted || !this.companyID) return;
      this.initStarted = true;
      this.loading = true;
      // Before the listener, so the first snapshot already maps these fields.
      // Never throws: an unreadable setting just means no extra columns.
      this.extraColumns = await resolveLaoExtraColumns(fc.db, this.companyID);
      // Also before the listener, which costs one round-trip but decides WHICH
      // month to open on. Opening on the calendar month and correcting
      // afterwards would mean two listeners and a table that flashes empty
      // first — and for a company whose last filing was months ago, the empty
      // one is what a user would have seen and read as a broken page.
      //
      // Both of these settle while extraColumnsReady is still false, so moving
      // monthFilter here cannot make its watcher open a listener that the
      // subscribe() below would immediately replace.
      await this.loadMonthRange();
      this.extraColumnsReady = true;
      this.subscribe();
    },
    teardown() {
      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }
    },
    subscribe() {
      if (!this.companyID) return;
      this.teardown();
      // Claim this generation before anything async: a snapshot from the
      // listener we just dropped can still arrive, and must be ignored.
      const generation = ++this.queryGeneration;
      this.loading = true;
      this.error = "";
      this.capHit = false;
      this.primed = false;
      this.rowsById = {};

      this.unsubscribe = buildApprovedQuery(fc.db, this.companyID, this.monthFilter).onSnapshot(
        (snap) => {
          if (generation !== this.queryGeneration) return;
          this.applySnapshot(snap);
          this.capHit = this.monthFilter === ALL && snap.size >= ALL_MONTHS_LIMIT;
          this.loading = false;
          this.error = "";
        },
        (err) => {
          if (generation !== this.queryGeneration) return;
          this.loading = false;
          this.error = describeQueryError(err);
        }
      );
    },
    // The first snapshot of a subscription is the whole month, so it is built
    // in one go and assigned once. Everything after it is a delta: the
    // verification poller approves in batches and the PSC Connector writes its
    // SAP fields back one document at a time, so this listener fires
    // repeatedly while the page is open. Re-mapping every row for each of
    // those — what `rows = snap.docs.map(toRow)` did — re-ran toRow() over the
    // whole month and invalidated every computed on the page to move one icon.
    applySnapshot(snap) {
      if (!this.primed) {
        const next = {};
        snap.docs.forEach((d) => {
          next[d.id] = this.toRow(d.id, d.data() || {});
        });
        this.rowsById = next;
        this.primed = true;
        return;
      }
      snap.docChanges().forEach((change) => {
        const id = change.doc.id;
        if (change.type === "removed") {
          this.$delete(this.rowsById, id);
          return;
        }
        // 'added' and 'modified' are the same write. $set is what makes a key
        // that was not there reactive in Vue 2.
        this.$set(this.rowsById, id, this.toRow(id, change.doc.data() || {}));
      });
    },
    // Two reads, once per page open: which months exist, and which one to
    // open on.
    async loadMonthRange() {
      const probe = await probeApprovedMonths(fc.db, this.companyID, thisMonth());
      // Only the descending read needs the Desc index, so this is what someone
      // sees after creating one of the two indexes and not the other. Say so
      // rather than quietly falling back to the calendar month — that failure
      // mode is indistinguishable from "this month is genuinely empty".
      this.monthRangeFailed = probe.failed;
      if (!probe.months.length) return;
      this.monthRange = probe.months;
      // Open on the newest month that actually HAS documents. For an active
      // company that is this month anyway.
      //
      // Guarded, because the descending probe can fail on its own: the list is
      // still usable then, but there is no newest month to open on and the
      // calendar month has to stand. The warning above says why.
      if (!probe.newest) return;
      this.newestMonth = probe.newest;
      this.defaultMonth = probe.newest;
      this.monthFilter = probe.newest;
    },
    toRow(id, data) {
      const issueDateTime = data["ED.IssueDateTime"] || "";
      const sapDb = data["@DB"] || {};
      const row = {
        id: data.objectID || id,
        issueDate: issueDateTime ? issueDateTime.split("T")[0] : "",
        docType: DOC_TYPE_CODE[data["@DocType"]] || String(data["@DocType"] || ""),
        buyerName: data["SCTT.AHTA.BTP.Name"] || "",
        buyerTaxID: data["SCTT.AHTA.BTP.STR.ID"] || "",
        grandTotal: data["SCTT.AHTS.STSHMS.GrandTotalAmount"] || 0,
        invoiceNumber: data["@LaoEtaxInvoiceNumber"] || "",
        issueTime: data["@LaoEtaxIssueTime"] || "",
        url: data["@LaoEtaxUrl"] || "",
        // Flattened once here rather than read off the document per render:
        // sapState is the table's sort and filter key, so it is touched for
        // every row on every keystroke.
        sapState: sapSyncState(data),
        sapTouched: sapIsTouched(data),
        sapError: sapSyncError(data),
        sapTransId: sapDb.SBO_OJDT_transId || "",
        sapTransType: sapDb.SBO_OJDT_transType,
      };
      this.extraColumns.forEach((c) => {
        const v = readFieldPath(data, c.key);
        row[extraProp(c)] = v === undefined || v === null ? "" : String(v);
      });
      return row;
    },
    typeMeta(code) {
      return DOC_TYPE_META[code] || { label: code, color: "#9e9e9e" };
    },
    monthLabel(month) {
      if (!month || month === ALL) return "ทุกเดือน";
      return moment(month, "YYYY-MM").locale("th").format("MMMM YYYY");
    },
    sapMeta(row) {
      return SAP_SYNC_META[row.sapState] || SAP_SYNC_META.pending;
    },
    sapTooltip(row) {
      return sapSyncTooltip(row);
    },
    sapLabel(state) {
      return sapSyncLabel(state);
    },
    // The icon carries the state, so it needs a name of its own. Colour alone
    // would not tell a screen reader apart from a sighted user which of the
    // three a row is in.
    sapAriaLabel(row) {
      return "การซิงค์ SAP: " + sapSyncLabel(row.sapState) + " (" + row.id + ")";
    },
    formatAmount(v) {
      return formatTwoDecimal(v) || "0.00";
    },
    // The gateway's own string; shown as-is when it is not a recognisable date.
    formatIssueTime(v) {
      if (!v) return "-";
      const m = moment(v, ["YYYY-MM-DD HH:mm:ss", moment.ISO_8601], true);
      return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : v;
    },
    clearFilters() {
      // Cancel the pending debounce, or it would land 300ms later and put the
      // keyword back.
      this.applyKeyword.cancel();
      this.keywordInput = "";
      this.keyword = "";
      this.typeFilter = ALL;
      // Back to this month — the page's resting state — not to ทุกเดือน. The
      // monthFilter watcher resubscribes if this actually changed it.
      this.monthFilter = this.defaultMonth;
      this.sapFilter = ALL;
    },
  },
};
</script>

<style scoped lang="scss">
// Mirrors #table-firebase in components/admin/documents/table.vue so the
// toolbar reads the same as the other document pages.
.submitted-docs {
  .search-box {
    width: 270px;
  }
  .month-box {
    width: 200px;
  }
  .sap-box {
    width: 190px;
  }
  // popoverMenu.vue (the กรอง / ซ่อนคอลัมน์ buttons on the other document
  // pages) ships an unscoped `.active { background: #6ec39b; color: #fff }`.
  // Vuetify puts `active` on the sorted column's <th>, so once any document
  // page has been opened in this session the sorted header here turned green —
  // and a refresh "fixed" it because popoverMenu's CSS was no longer loaded.
  // Restore Vuetify's own header look.
  ::v-deep .v-data-table-header th.active {
    background-color: transparent;
    color: rgba(0, 0, 0, 0.87);
  }
  // Vuetify 2 sets no white-space on header cells, and its sort arrow
  // (.v-data-table-header__icon) is an inline element that keeps its box at
  // opacity 0 until the column is hovered or sorted. So a header whose label
  // and arrow do not both fit wraps the arrow onto a second line — visible as
  // a blank second row of header most of the time, since the arrow itself is
  // transparent. One line, always.
  ::v-deep .v-data-table-header th {
    white-space: nowrap;
  }
  // One height for the whole toolbar: dense outlined inputs are 40px, so the
  // buttons are set to 40 too, with the inputs' 10px corners.
  .toolbar-btn {
    border-radius: 10px;
  }
  ::v-deep .v-text-field fieldset,
  ::v-deep .v-text-field .v-input__control,
  ::v-deep .v-text-field .v-input__slot {
    border-radius: 10px !important;
    border-color: #c4c4c4;
  }
}

.submitted-docs__chip--active {
  border-color: #6ec39b !important;
  color: #6ec39b !important;
}

.submitted-docs__clip {
  display: inline-block;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.submitted-docs__num {
  font-variant-numeric: tabular-nums;
}
</style>
